/**
 * Shared HTTP layer for the CDB-TQS API modules.
 * Uses MSAL Bearer token for authentication; all requests go through the
 * BFF proxy at /api/v1/tqs/*.
 */

import { logApiCall, logApiResponse, logError, logWarn, logInfo } from "@/lib/logger";
import { getAccessToken } from "@/services/auth-service";
import { env } from "@/lib/env";

/**
 * Get MIME type from filename extension
 * @param filename - The filename to check
 * @returns MIME type string or undefined if unknown
 */
export function getMimeTypeFromFilename(filename: string): string | undefined {
  const extension = filename.toLowerCase().split('.').pop();
  if (!extension) return undefined;

  const mimeTypes: Record<string, string> = {
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // Excel
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
    xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    csv: 'text/csv',
    // PowerPoint
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Text
    txt: 'text/plain',
    md: 'text/markdown',
    json: 'application/json',
    xml: 'application/xml',
    yaml: 'text/yaml',
    yml: 'text/yaml',
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    // Audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    m4a: 'audio/mp4',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
    // Video
    mp4: 'video/mp4',
    m4v: 'video/x-m4v',
    mov: 'video/quicktime',
    webm: 'video/webm',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    // Archives
    zip: 'application/zip',
    rar: 'application/vnd.rar',
    '7z': 'application/x-7z-compressed',
    tar: 'application/x-tar',
    gz: 'application/gzip',
  };

  return mimeTypes[extension];
}

// Get BFF API base URL from environment variable
// All TQS requests now go through the BFF proxy at /api/v1/tqs/*
const BACKEND_API_URL = env.backendApiUrl;

// Add https:// if no protocol is specified
const withProtocol =
  BACKEND_API_URL.startsWith("http://") || BACKEND_API_URL.startsWith("https://")
    ? BACKEND_API_URL
    : `https://${BACKEND_API_URL}`;

const normalizedApiBase = withProtocol.replace(/\/+$/, "");

// TQS requests go through BFF proxy at /api/v1/tqs
export const CDB_TQS_API_BASE_URL = `${normalizedApiBase}/api/v1/tqs`;

export interface RequestOptions extends RequestInit {
  /** A string[] becomes a repeated query parameter (?status=a&status=b), which
   *  is how FastAPI reads a list — not a comma-joined single value. */
  params?: Record<string, string | boolean | string[]>;
}

/**
 * Fehler einer cdb_tqs-Antwort mit erhaltenem Statuscode (#516).
 *
 * Bis hierher warf `fetchWithLogging` ein blankes `Error`, dessen Status nur im
 * Text stand. Wer "kein Zugriff" von "Backend gerade nicht erreichbar"
 * unterscheiden muss — der Dokument-Resolver tut das — hätte diesen Text
 * zerlegen müssen. Die Message bleibt unverändert, damit bestehende Aufrufer
 * nichts merken.
 */
/** One entry of `detail.errors`, e.g. `{path: "entity_types[3].fields[1].name", message}`. */
export interface CdbTqsFieldError {
  path: string;
  message: string;
}

export class CdbTqsApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** `detail.code` when cdb_tqs answered `{detail: {code, message}}`, e.g. 409 `client_has_knowledge_areas`. */
    readonly code: string | null = null,
    /** `detail.errors` next to code and message, e.g. the 422 `ontology_invalid` list. */
    readonly errors: CdbTqsFieldError[] = [],
  ) {
    super(message);
    this.name = "CdbTqsApiError";
  }
}

function readFieldErrors(raw: unknown): CdbTqsFieldError[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e): e is { path?: unknown; message: unknown } => typeof e?.message === "string")
    .map((e) => ({ path: typeof e.path === "string" ? e.path : "", message: e.message as string }));
}

/**
 * Get auth headers for cdb_tqs backend
 * Uses Bearer token for authentication (Azure AD)
 * Automatically attempts token refresh if expired
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    // First try to get existing token
    let token = getAccessToken();

    // If no token, attempt to refresh
    if (!token) {
      const { authService } = await import("@/services/auth-service");
      token = await authService.refreshAccessToken();
    }

    if (token) {
      return {
        Authorization: `Bearer ${token}`,
      };
    }

    // No token after refresh - log warning
    logError(new Error("No access token available after refresh attempt"), {
      context: "CdbTqsApiService.getAuthHeaders",
    });
    return {};
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), {
      context: "CdbTqsApiService.getAuthHeaders",
    });
    return {};
  }
}

/**
 * Execute a single fetch request with auth headers, logging and response parsing.
 */
async function executeFetch(
  url: string,
  method: string,
  options: RequestOptions,
  authHeaders: Record<string, string>,
  startTime: number,
) {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options.headers,
    },
  });

  const duration = performance.now() - startTime;

  // Parse response - handle empty responses (e.g., DELETE)
  let data: any = null;
  const contentType = response.headers.get("content-type");
  const contentLength = response.headers.get("content-length");

  if (contentLength !== "0" && response.status !== 204) {
    try {
      if (contentType?.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text || null;
      }
    } catch {
      data = null;
    }
  }

  logApiResponse(method, url, response.status, data);

  if (duration > 1000) {
    logApiResponse(method, url, response.status, { duration, slow: true });
  }

  return { response, data, duration };
}

// Keys whose values must never reach the debug log (create/rotate send
// plaintext DB passwords; logApiCall dumps the request body 1:1 when
// VITE_DEBUG_MODE is on). Matches password / *_password / secret / token /
// credential / api_key (any casing/separator), at any nesting depth.
const SENSITIVE_KEY_RE = /pass(word)?|secret|token|credential|api[_-]?key/i

function redactValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactValue)
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        k,
        SENSITIVE_KEY_RE.test(k) ? "[REDACTED]" : redactValue(v),
      ]),
    )
  }
  return value
}

/**
 * Redact sensitive fields (passwords, secrets, tokens, api keys) from a request
 * body before it is written to the debug log. Bodies are JSON strings here; a
 * non-string body (FormData, etc.) or non-JSON string is returned unchanged.
 */
function redactBodyForLog(body: unknown): unknown {
  if (typeof body !== "string") return body
  // Fast path: the vast majority of bodies carry nothing sensitive — a cheap
  // substring test avoids paying JSON.parse + deep-clone on every API call
  // (this runs regardless of whether the log line is ultimately emitted).
  if (!SENSITIVE_KEY_RE.test(body)) return body
  try {
    return redactValue(JSON.parse(body))
  } catch {
    return body
  }
}

/**
 * Enhanced fetch wrapper with logging, automatic JWT authentication,
 * and 401 retry with token refresh.
 */
export async function fetchWithLogging(url: string, options: RequestOptions = {}) {
  const startTime = performance.now();
  const method = options.method || "GET";

  // Add query parameters if provided
  if (options.params && Object.keys(options.params).length > 0) {
    const params = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => params.append(key, entry));
      } else {
        params.append(key, String(value));
      }
    });
    url = `${url}?${params.toString()}`;
  }

  const authHeaders = await getAuthHeaders();

  logApiCall(method, url, redactBodyForLog(options.body));

  try {
    let { response, data, duration } = await executeFetch(url, method, options, authHeaders, startTime);

    // 401 Retry: refresh token and retry once
    if (response.status === 401) {
      logWarn({ context: "fetchWithLogging", method, url }, "401 received, attempting token refresh and retry");
      const { authService } = await import("@/services/auth-service");
      const newToken = await authService.refreshAccessToken();

      if (newToken) {
        logInfo({ context: "fetchWithLogging" }, "Token refreshed, retrying request");
        const retryHeaders = { Authorization: `Bearer ${newToken}` };
        const retryResult = await executeFetch(url, method, options, retryHeaders, startTime);
        response = retryResult.response;
        data = retryResult.data;
        duration = retryResult.duration;
      }
    }

    if (!response.ok) {
      logError(new Error(`API ${response.status}`), {
        context: "fetchWithLogging",
        method,
        url,
        status: response.status,
        detail: data?.detail,
      });

      let errorDetail = response.statusText;
      let errorCode: string | null = null;
      let fieldErrors: CdbTqsFieldError[] = [];
      if (data?.detail) {
        if (Array.isArray(data.detail)) {
          // FastAPI validation errors are arrays of objects
          errorDetail = data.detail
            .map((err: any) => `${err.loc?.join(".")} - ${err.msg}`)
            .join("; ");
        } else if (typeof data.detail === "object" && typeof data.detail.message === "string") {
          errorDetail = data.detail.message;
          errorCode = typeof data.detail.code === "string" ? data.detail.code : null;
          fieldErrors = readFieldErrors(data.detail.errors);
        } else {
          errorDetail = data.detail;
        }
      } else if (data?.message) {
        errorDetail = data.message;
      }

      throw new CdbTqsApiError(
        response.status,
        `CDB-TQS API Error: ${response.status} ${errorDetail}`,
        errorCode,
        fieldErrors,
      );
    }

    return { data, status: response.status, duration };
  } catch (error) {
    const duration = performance.now() - startTime;

    logError(error as Error, {
      method,
      url,
      duration,
      context: "CDB-TQS API call failed",
    });

    throw error;
  }
}
